// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useCallback, useEffect, useState } from "react";
import { App, Button, List, Spin, Tag, Typography } from "antd";
import { DesktopOutlined } from "@ant-design/icons";
import type { DeviceInfo, Homeserver } from "../api";
import type { Session } from "../types";

// DevicesPanel is the "logged-in devices" manager (Keybase/WhatsApp/Telegram-style):
// see every device signed in to your account, rename one, and SIGN OUT (revoke) a
// device you no longer use — which kills its access immediately and drops it from the
// key directory. Pairs with the key backup, which lets a NEW device read old history.
export function DevicesPanel({ session, hs }: { readonly session: Session; readonly hs: Homeserver }) {
  const { message, modal } = App.useApp();
  const [devices, setDevices] = useState<DeviceInfo[] | null>(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    hs.devices(session.access_token)
      .then(setDevices)
      .catch(() => setDevices([]));
  }, [hs, session.access_token]);
  useEffect(load, [load]);

  const short = (id: string) => id.split("/").pop() ?? id;
  const fmt = (ms: number) => new Date(ms).toLocaleString();

  const revoke = (d: DeviceInfo) => {
    modal.confirm({
      title: `Sign out "${d.display_name || short(d.device_id)}"?`,
      content: "That device is signed out immediately and can't read new messages until it logs in again.",
      okText: "Sign out device",
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusy(d.device_id);
        try {
          await hs.revokeDevice(session.access_token, d.device_id);
          message.success("Device signed out");
          load();
        } catch {
          message.error("Could not sign out that device");
        } finally {
          setBusy("");
        }
      },
    });
  };

  const rename = async (d: DeviceInfo, name: string) => {
    const next = name.trim();
    if (!next || next === d.display_name) return;
    try {
      await hs.renameDevice(session.access_token, d.device_id, next);
      load();
    } catch {
      message.error("Rename failed");
    }
  };

  if (devices === null) return <Spin />;
  if (!devices.length) return <Typography.Text type="secondary">No devices.</Typography.Text>;

  // Current device first, then most-recently-active.
  const sorted = [...devices].sort((a, b) => {
    if (a.current !== b.current) return a.current ? -1 : 1;
    return (b.last_seen ?? b.created_at) - (a.last_seen ?? a.created_at);
  });

  return (
    <List
      itemLayout="horizontal"
      dataSource={sorted}
      renderItem={(d) => (
        <List.Item
          actions={
            d.current
              ? [
                  <Tag color="green" key="cur">
                    This device
                  </Tag>,
                ]
              : [
                  <Button key="revoke" danger size="small" loading={busy === d.device_id} onClick={() => revoke(d)}>
                    Sign out
                  </Button>,
                ]
          }
        >
          <List.Item.Meta
            avatar={<DesktopOutlined style={{ fontSize: 20 }} />}
            title={<Typography.Text editable={{ onChange: (v) => void rename(d, v), tooltip: "Rename device" }}>{d.display_name || short(d.device_id)}</Typography.Text>}
            description={
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {d.last_seen ? `Last active ${fmt(d.last_seen)}` : `Added ${fmt(d.created_at)}`}
                {d.fingerprint ? ` · key ${d.fingerprint.slice(0, 10)}…` : ""}
              </Typography.Text>
            }
          />
        </List.Item>
      )}
    />
  );
}
